import { useState } from "react";
import { Link } from "react-router";
import { Badge, Button, Dialog, EmptyState, Field, IconButton, Input, Select } from "@agentsaas/ui";
import { pickLocalized, type Capability, type CapabilityRef, type McpScope } from "@agentsaas/protocol";
import { useT } from "../app/i18n";
import { refKey, useCapabilities, useCapabilityVersions, useMcpServers, useResolvedRefs } from "../data/capabilities";
import { CapIcon } from "./common";
import { Loading } from "../layout/Loading";
import { mergeCapabilities, toolCount, type MergeResult } from "./model";

/**
 * 模板编辑器里的"能力包"区：选择器（搜索能力库添加）、每个引用可固定版本并填写参数、调整顺序（后引用的 skill 覆盖先引用的）；
 * 下方是合并后的效果预览（skill 列表、工具、镜像、出网域名），镜像冲突时给出提示。
 */

type ParamValue = string | number | boolean;

export function useMergePreview(tid: string, refs: CapabilityRef[], base: { mcpScopes: McpScope[]; allowedTools: string[]; image: string }) {
  const resolved = useResolvedRefs(tid, refs);
  const inputs = refs.flatMap((ref) => {
    const cap = resolved.map.get(refKey(ref));
    return cap ? [{ ref, cap }] : [];
  });
  const merged = mergeCapabilities(base, inputs);
  return { merged, resolved };
}

export function TemplateCapabilities({
  tid,
  value,
  onChange,
  readOnly,
  base,
}: {
  tid: string;
  value: CapabilityRef[];
  onChange(v: CapabilityRef[]): void;
  readOnly?: boolean;
  base: { mcpScopes: McpScope[]; allowedTools: string[]; image: string };
}) {
  const { t } = useT();
  const [picking, setPicking] = useState(false);
  const { merged, resolved } = useMergePreview(tid, value, base);
  const update = (i: number, patch: Partial<CapabilityRef>) => onChange(value.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i: number, d: -1 | 1) => {
    const next = [...value];
    const [x] = next.splice(i, 1);
    next.splice(i + d, 0, x!);
    onChange(next);
  };
  return (
    <fieldset className="tpl-caps" data-testid="tpl-caps">
      <legend className="ui-field__label">{t("tplcap.title")}</legend>
      <p className="ui-field__hint">{t("tplcap.hint")}</p>
      {value.length ? (
        <ul className="cap-refs">
          {value.map((r, i) => (
            <RefItem
              key={r.id}
              tid={tid}
              value={r}
              cap={resolved.map.get(refKey(r))}
              missing={resolved.missing.has(r.id)}
              readOnly={readOnly}
              first={i === 0}
              last={i === value.length - 1}
              onChange={(p) => update(i, p)}
              onMove={(d) => move(i, d)}
              onRemove={() => onChange(value.filter((_, j) => j !== i))}
            />
          ))}
        </ul>
      ) : (
        <p className="muted small">{t("tplcap.empty")}</p>
      )}
      {!readOnly ? (
        <div className="row">
          <Button size="sm" icon="plus" onClick={() => setPicking(true)} data-testid="tplcap-add">
            {t("tplcap.add")}
          </Button>
          <Link to={`/t/${tid}/capabilities`} className="small">
            {t("tplcap.browse")}
          </Link>
        </div>
      ) : null}
      <MergePreview tid={tid} merged={merged} caps={resolved.map} refs={value} />
      <CapabilityPicker
        tid={tid}
        open={picking}
        exclude={value.map((r) => r.id)}
        onClose={() => setPicking(false)}
        onPick={(id) => {
          onChange([...value, { id, version: null, params: {} }]);
          setPicking(false);
        }}
      />
    </fieldset>
  );
}

function RefItem({
  tid,
  value,
  cap,
  missing,
  readOnly,
  first,
  last,
  onChange,
  onMove,
  onRemove,
}: {
  tid: string;
  value: CapabilityRef;
  cap: Capability | undefined;
  missing: boolean;
  readOnly?: boolean;
  first: boolean;
  last: boolean;
  onChange(p: Partial<CapabilityRef>): void;
  onMove(d: -1 | 1): void;
  onRemove(): void;
}) {
  const { t, locale } = useT();
  const versions = useCapabilityVersions(tid, value.id);
  const setParam = (k: string, v: ParamValue | null) => {
    const params = { ...value.params };
    if (v === null || v === "") delete params[k];
    else params[k] = v;
    onChange({ params });
  };
  const name = cap ? pickLocalized(cap.manifest.name, locale) : value.id;
  return (
    <li className="cap-ref" data-testid="tplcap-item" data-cap={value.id}>
      <div className="cap-ref__head">
        <CapIcon icon={cap?.manifest.icon ?? "sparkles"} size={16} />
        <Link to={`/t/${tid}/capabilities/${value.id}${value.version ? `?v=${value.version}` : ""}`} className="grow ellipsis">
          <strong>{name}</strong>
        </Link>
        <code className="muted small">{value.id}</code>
        {missing ? <Badge tone="danger">{t("tplcap.missing")}</Badge> : null}
        {cap?.status === "deprecated" ? <Badge tone="warning">{t("cap.deprecated")}</Badge> : null}
        <Select
          aria-label={t("tplcap.version_of", { name })}
          value={value.version === null ? "" : String(value.version)}
          disabled={readOnly}
          onChange={(e) => onChange({ version: e.target.value ? Number(e.target.value) : null })}
          data-testid="tplcap-version"
        >
          <option value="">{t("tplcap.follow_latest")}</option>
          {(versions.data ?? []).map((v) => (
            <option key={v.version} value={v.version}>
              {t("tplcap.pin", { version: v.version })}
            </option>
          ))}
        </Select>
        {!readOnly ? (
          <>
            <IconButton icon="chevronUp" label={t("tplcap.move_up", { name })} disabled={first} onClick={() => onMove(-1)} data-testid="tplcap-up" />
            <IconButton icon="chevronDown" label={t("tplcap.move_down", { name })} disabled={last} onClick={() => onMove(1)} data-testid="tplcap-down" />
            <IconButton icon="trash" label={t("tplcap.remove", { name })} onClick={onRemove} data-testid="tplcap-remove" />
          </>
        ) : null}
      </div>
      {cap ? <p className="small muted">{pickLocalized(cap.manifest.description, locale)}</p> : null}
      {cap?.manifest.params.length ? (
        <div className="cap-form__pair">
          {cap.manifest.params.map((p) => {
            const cur = value.params[p.key];
            const label = pickLocalized(p.label, locale);
            const hint = p.default !== undefined ? t("tplcap.param_default", { value: String(p.default) }) : t("tplcap.param_required");
            return (
              <Field key={p.key} label={label} hint={hint}>
                {(id, desc) =>
                  p.type === "boolean" ? (
                    <Select id={id} aria-describedby={desc} disabled={readOnly} value={cur === undefined ? "" : String(cur)} onChange={(e) => setParam(p.key, e.target.value === "" ? null : e.target.value === "true")}>
                      <option value="">{t("tplcap.param_use_default")}</option>
                      <option value="true">{t("cap.form.yes")}</option>
                      <option value="false">{t("cap.form.no")}</option>
                    </Select>
                  ) : p.type === "enum" ? (
                    <Select id={id} aria-describedby={desc} disabled={readOnly} value={cur === undefined ? "" : String(cur)} onChange={(e) => setParam(p.key, e.target.value || null)} data-testid={`tplcap-param-${p.key}`}>
                      <option value="">{t("tplcap.param_use_default")}</option>
                      {(p.options ?? []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Input
                      id={id}
                      aria-describedby={desc}
                      disabled={readOnly}
                      type={p.type === "number" ? "number" : "text"}
                      value={cur === undefined ? "" : String(cur)}
                      onChange={(e) => setParam(p.key, p.type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
                      data-testid={`tplcap-param-${p.key}`}
                    />
                  )
                }
              </Field>
            );
          })}
        </div>
      ) : null}
    </li>
  );
}

export function MergePreview({ tid, merged, caps, refs }: { tid: string; merged: MergeResult; caps: Map<string, Capability>; refs: CapabilityRef[] }) {
  const { t, locale } = useT();
  const servers = useMcpServers(tid);
  const nameOf = (id: string) => {
    const r = refs.find((x) => x.id === id);
    const c = r ? caps.get(refKey(r)) : undefined;
    return c ? pickLocalized(c.manifest.name, locale) : id;
  };
  const none = <span className="muted">{t("cap.none")}</span>;
  return (
    <section className="card cap-preview" aria-labelledby="tplcap-preview" data-testid="tplcap-preview">
      <h3 className="section-title" id="tplcap-preview">
        {t("tplcap.preview")}
      </h3>
      {merged.imageConflict ? (
        <div className="banner banner--danger" role="alert" data-testid="tplcap-conflict">
          <span>{t("tplcap.image_conflict")}</span>
          <ul className="small">
            {merged.imageConflict.map((x) => (
              <li key={x.capabilityId}>
                <strong>{nameOf(x.capabilityId)}</strong>: <code>{x.image}</code>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {merged.overridden.map((o) => (
        <p key={`${o.name}:${o.by}`} className="banner banner--warning small" role="status">
          {t("tplcap.skill_overridden", { skill: o.name, from: nameOf(o.from), by: nameOf(o.by) })}
        </p>
      ))}
      {merged.missingParams.map((m) => (
        <p key={`${m.capabilityId}:${m.key}`} className="banner banner--warning small" role="status">
          {t("tplcap.param_missing", { cap: nameOf(m.capabilityId), key: m.key })}
        </p>
      ))}
      <dl className="kv">
        <dt>{t("cap.skills")}</dt>
        <dd data-testid="tplcap-preview-skills">
          {merged.skills.length
            ? merged.skills.map((s) => (
                <code key={s.name} className="cap-pill" title={nameOf(s.capabilityId)}>
                  {s.name}
                </code>
              ))
            : none}
        </dd>
        <dt>{t("cap.tools.scopes")}</dt>
        <dd data-testid="tplcap-preview-scopes">{merged.mcpScopes.length ? merged.mcpScopes.map((s) => <code key={s} className="cap-pill">{s}</code>) : none}</dd>
        <dt>{t("cap.tools.allowed")}</dt>
        <dd>{merged.allowedTools.length ? merged.allowedTools.map((s) => <code key={s} className="cap-pill">{s}</code>) : none}</dd>
        <dt>{t("cap.tools.external")}</dt>
        <dd>
          {merged.externalMcp.length
            ? merged.externalMcp.map((x) => (
                <code key={x.name} className="cap-pill">
                  {x.name}
                  <span className="muted"> {servers.data?.find((s) => s.id === x.serverId)?.name ?? x.serverId}</span>
                </code>
              ))
            : none}
        </dd>
        <dt>{t("cap.runtime.image")}</dt>
        <dd data-testid="tplcap-preview-image">{merged.imageConflict ? <Badge tone="danger">{t("tplcap.image_unresolved")}</Badge> : <code>{merged.image}</code>}</dd>
        <dt>{t("cap.runtime.egress")}</dt>
        <dd>{merged.egress.length ? merged.egress.map((s) => <code key={s} className="cap-pill">{s}</code>) : none}</dd>
      </dl>
    </section>
  );
}

/** 从能力库挑一个能力包（搜索；已添加 / 已下线的不可选） */
export function CapabilityPicker({ tid, open, exclude, onClose, onPick }: { tid: string; open: boolean; exclude: string[]; onClose(): void; onPick(id: string): void }) {
  const { t, locale } = useT();
  const [q, setQ] = useState("");
  const list = useCapabilities(open ? tid : "");
  // 每次打开都从空搜索开始
  const close = () => {
    setQ("");
    onClose();
  };
  const pick = (id: string) => {
    setQ("");
    onPick(id);
  };
  const needle = q.trim().toLowerCase();
  const items = (list.data ?? []).filter(
    (c) => !needle || [c.id, ...Object.values(c.manifest.name), ...Object.values(c.manifest.description)].some((s) => s?.toLowerCase().includes(needle)),
  );
  return (
    <Dialog open={open} onClose={close} title={t("tplcap.pick_title")} closeLabel={t("shell.close")} size="md">
      <div className="stack">
        <Input type="search" aria-label={t("cap.search")} placeholder={t("cap.search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="tplcap-search" autoFocus />
        {list.isPending ? (
          <Loading />
        ) : items.length ? (
          <ul className="cap-picker" data-testid="tplcap-picker">
            {items.map((c) => {
              const added = exclude.includes(c.id);
              const off = added || c.status === "deprecated";
              return (
                <li key={c.id}>
                  <button type="button" className="cap-picker__item" disabled={off} onClick={() => pick(c.id)} data-testid="tplcap-option" data-cap={c.id}>
                    <CapIcon icon={c.manifest.icon} size={16} />
                    <span className="grow">
                      <strong>{pickLocalized(c.manifest.name, locale)}</strong>
                      <span className="muted small"> {c.id}</span>
                      <span className="small muted cap-picker__desc"> {t("cap.skills_count", { count: c.manifest.skills.length })}, {t("cap.tools_count", { count: toolCount(c.manifest) })}</span>
                    </span>
                    {added ? <Badge>{t("tplcap.added")}</Badge> : c.status === "deprecated" ? <Badge tone="warning">{t("cap.deprecated")}</Badge> : c.tenantId ? <Badge tone="accent">{t("cap.custom")}</Badge> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState icon="sparkles" title={t("cap.empty")} />
        )}
      </div>
    </Dialog>
  );
}
